import { TableColumn } from "PFApp/components/table/table_types";
import { useTemplate } from "PFApp/hooks";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { getTableColumnsFromProperties, getTemplateVisibleProperties } from "../../helpers";

export const useEngagementsColumns = (blockedProperties: string[]) => {
  const { data: currentProfile } = useCurrentProfile();
  const { customTypes } = useCustomTypes();
  const { t } = useTranslation("workflow", { keyPrefix: "parts.engagements.columns" });
  const engagementTemplate = useTemplate({ key: "engagement" });

  const defaultColumns = useMemo<TableColumn[]>(
    () => [
      {
        name: "name",
        title: t("engagementTitle"),
        isDefault: true,
        locked: true,
        size: 3,
        type: "text",
        orderKey: "activity.name"
      },
      {
        name: "roles",
        title: t("roles"),
        size: 1,
        type: "text"
      },
      { name: "status", title: t("status"), size: 2, type: "text" },
      {
        name: "post_until",
        title: t("resourcingCompletedBy"),
        size: 2,
        type: "date-time"
      },
      {
        name: "assignee",
        title: t("assignee"),
        size: 2,
        type: "profile"
      },
      {
        name: "due",
        attribute: ["post_until"],
        title: t("dueInDays"),
        size: 2,
        type: "text",
        orderKey: "activity.due"
      },
      {
        name: "start_date",
        attribute: ["duration"],
        title: t("startDate"),
        size: 2,
        type: "date",
        orderKey: "activity.start_date"
      },
      {
        name: "end_date",
        attribute: ["duration"],
        title: t("endDate"),
        size: 2,
        type: "date",
        orderKey: "activity.end_date"
      },
      {
        name: "duration",
        attribute: ["number_of_days"],
        title: t("duration"),
        size: 1,
        type: "noOfDays",
        orderKey: "activity.number_of_days"
      },
      {
        name: "created_at",
        title: t("dateCreated"),
        size: 2,
        type: "date",
        orderKey: "activity.saas_created_at"
      },
      {
        name: "principal_owner",
        title: t("principalOwner"),
        size: 2,
        type: "profile"
      }
    ],
    [t]
  );

  const visibleTemplateProperties = useMemo(
    () => getTemplateVisibleProperties(customTypes, currentProfile, blockedProperties, engagementTemplate),
    [currentProfile, customTypes, engagementTemplate, blockedProperties]
  );

  const availableColumns = useMemo(() => {
    const columnsFromCustomTypes = getTableColumnsFromProperties(visibleTemplateProperties);

    return defaultColumns.concat(columnsFromCustomTypes as TableColumn[]);
  }, [visibleTemplateProperties, defaultColumns]);

  return { defaultColumns, availableColumns };
};
